
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Round a monetary value to cent (2-decimal) precision, stripping IEEE-754
 * artifacts such as `882.6719999999999` → `882.67`. Apply before storing or
 * displaying any currency amount. Sign-symmetric; non-finite values pass through.
 */
export function roundToCents(value: number): number {
  if (!Number.isFinite(value)) return value;
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round((Math.abs(value) + Number.EPSILON) * 100)) / 100;
}

export type CurrencyFormat = 'default' | 'compact';

export function formatCurrency(value: number, format: CurrencyFormat = 'default'): string {
  if (format === 'compact') {
    const absValue = Math.abs(value);
    let formattedValue: string;
    
    if (absValue >= 1000000) {
      formattedValue = (value / 1000000).toFixed(1) + 'M';
    } else if (absValue >= 1000) {
      formattedValue = (value / 1000).toFixed(1) + 'k';
    } else {
      formattedValue = value.toFixed(0);
    }
    
    return '€' + formattedValue;
  }
  
  // Use space as thousands separator for better readability
  const formatted = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `€${formatted}`;
}

/**
 * Format a percentage value while intelligently handling decimal places
 * - Preserves up to 2 decimal places if they exist
 * - Removes trailing zeros
 * - Adds % symbol
 */
export function formatPercentage(value: number): string {
  // Format with 2 decimal places, then remove trailing zeros
  return value.toFixed(2).replace(/\.?0+$/, '') + '%';
}
